import React from 'react';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import SpinnerButton from '../Common/SpinnerButton';
import { useForm } from 'react-hook-form';
import QueryView from '../Common/QueryView';
import useGetPurchaseOrderSettings, { PoCompletionType, PurchaseOrderSettingsDto } from '../Requests/PurchaseOrders/useGetPurchaseOrderSettings';
import useUpdatePurchaseOrderSettings from '../Requests/PurchaseOrders/useUpdatePurchaseOrderSettings';

interface PoCompletionForm {
  percentageEnabled: boolean;
  percentage: number;
}

const PurchaseOrderSettingsPage = () => {
  const getPoSettings = useGetPurchaseOrderSettings();

  return <QueryView
    query={getPoSettings}
    renderData={settings => <Form settings={settings} />} />
}

const Form = ({ settings }: { settings: PurchaseOrderSettingsDto }) => {
  const displayMessage = useDisplayMessage();

  const { register, handleSubmit, watch, getValues, formState: { errors } } = useForm<PoCompletionForm>({
    defaultValues: {
      percentageEnabled: settings.type === PoCompletionType.Percentage,
      percentage: settings.percentage
    }
  });

  const handleSave = (formValues: PoCompletionForm) => {
    useUpdatePoCompletion.request({
      type: formValues.percentageEnabled
        ? PoCompletionType.Percentage
        : PoCompletionType.Manual,
      percentage: formValues.percentageEnabled
        ? formValues.percentage
        : undefined,
    })
  }

  const useUpdatePoCompletion = useUpdatePurchaseOrderSettings({
    onSuccess: () => displayMessage.success("Purchase Order Completion settings have been updated."),
    onError: (e) => displayMessage.fail(e.message),
  });

  return (<>
    <h3 className="ka-blue">Purchase Order Settings</h3>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <div className="row border m-2 p-2">
      <div className="col">
        <div className="row">
          <label className="col" htmlFor='poCompletionPercentageInput'>
            Purchase Order Completion Percentage
          </label>
          <div className="col form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              {...register('percentageEnabled')}
            />
          </div>
        </div>
      </div>
      <div className="col">
        <div className="input-group">
          <input
            id='poCompletionPercentageInput'
            type="number"
            disabled={!watch('percentageEnabled')}
            className={`form-control ${!!errors.percentage && 'is-invalid'}`}
            {...register('percentage', {
              valueAsNumber: true,
              required: 'Purchase Order Completion Percentage required',
              validate: {
                positive: v => getValues('percentageEnabled')
                  ? v > 0 || 'Must be greater than 0'
                  : true
              },
            })}
          />
          <div className="input-group-append input-group-text">
            %
          </div>
          <span className="invalid-feedback">{errors.percentage?.message}</span>
        </div>
      </div>
    </div>
    <div className="float-end">
      <SpinnerButton
        className="btn-ghost-primary"
        spinning={useUpdatePoCompletion.isLoading}
        onClick={handleSubmit(handleSave)} >
        Save
      </SpinnerButton>
    </div>
  </>);
}

export { PurchaseOrderSettingsPage };
